import React from 'react';
import clsx from 'clsx';
import { Card, Progress } from '@randompedestrian07/ui';

interface StatCardProps {
    title: string;
    watermarkIcon?: React.ReactNode;
    children: React.ReactNode;
    footer?: React.ReactNode;
    className?: string;
    colSpan?: 1 | 2;
}

export const StatCard: React.FC<StatCardProps> = ({
    title,
    watermarkIcon,
    children,
    footer,
    className,
    colSpan = 1,
}) => {
    return (
        <Card
            className={clsx(
                "p-8 border-stroke bg-panel/30 backdrop-blur-md rounded-[2.5rem] shadow-2xl relative overflow-hidden group",
                colSpan === 2 && "lg:col-span-2",
                className
            )}
        >
            {watermarkIcon && (
                <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none">
                    {React.isValidElement(watermarkIcon)
                        ? React.cloneElement(watermarkIcon as React.ReactElement<{ size?: number }>, { size: 120 })
                        : watermarkIcon}
                </div>
            )}
            <div className="text-[10px] font-black uppercase tracking-[0.2em] text-muted/60 mb-8 relative z-10">
                {title}
            </div>
            <div className="space-y-6 relative z-10">
                {children}
                {footer}
            </div>
        </Card>
    );
};

interface StatValueProps {
    label: string;
    value: React.ReactNode;
    subLabel?: React.ReactNode;
    valueClassName?: string;
    showProgress?: boolean;
    progressValue?: number;
    trend?: React.ReactNode;
    indicatorColor?: string;
}

export const StatValue: React.FC<StatValueProps> = ({
    label,
    value,
    subLabel,
    valueClassName = 'text-text',
    showProgress = false,
    progressValue = 0,
    trend,
    indicatorColor,
}) => {
    return (
        <div className="space-y-4">
            {indicatorColor && (
                <div className="flex items-center gap-3">
                    <div className={clsx("w-2 h-2 rounded-full", indicatorColor)} />
                    <span className="text-[9px] font-black text-muted/40 uppercase tracking-widest">{label}</span>
                </div>
            )}
            <div className="flex justify-between items-end">
                {!indicatorColor && <span className="text-sm font-bold text-text/80">{label}</span>}
                {subLabel && <span className="text-sm font-bold text-text/80">{subLabel}</span>}
                <span className={clsx("text-3xl font-black", valueClassName)}>{value}</span>
            </div>
            {showProgress && <Progress value={progressValue} className="h-1.5 bg-panel-soft" />}
            {trend && <div className="text-[10px] font-bold text-muted/30 uppercase">{trend}</div>}
        </div>
    );
};

interface StatMetricProps {
    label: string;
    value: React.ReactNode;
    valueClassName?: string;
    icon?: React.ReactNode;
    trend?: 'up' | 'down' | 'neutral';
}

export const StatMetric: React.FC<StatMetricProps> = ({
    label,
    value,
    valueClassName = 'text-text',
    icon,
    trend,
}) => {
    return (
        <div className="flex justify-between items-center bg-panel-strong/40 rounded-2xl p-4 border border-stroke/20 shadow-inner">
            <div className="flex flex-col">
                <span className="text-[9px] font-black text-muted/40 uppercase">{label}</span>
                <span className={clsx("text-xl font-bold", trend === 'up' ? 'text-green' : trend === 'down' ? 'text-red' : valueClassName)}>{value}</span>
            </div>
            {icon && (
                <div className={clsx("text-current opacity-20", trend === 'up' ? 'text-green' : trend === 'down' ? 'text-red' : '')}>
                    {React.isValidElement(icon)
                        ? React.cloneElement(icon as React.ReactElement<{ size?: number }>, { size: 24 })
                        : icon}
                </div>
            )}
        </div>
    );
};
